/**
 * CompactDocumentGrid — compact document tile grid for SalesDetail.
 *
 * Replaces the large SaleUploads dropzones with small uniform cards grouped by
 * domain (Customer / Vehicle / Finance / PDI / Delivery / Other). Each card
 * shows the document name + uploaded-by + timestamp + Preview/Download/Replace.
 * An empty slot becomes a "+ Upload" tile. Mandatory documents get a red pill.
 *
 * Workflow + permissions stay identical to the legacy SaleUploads component —
 * only the visual treatment changes.
 */
import React, { useEffect, useRef, useState, useMemo } from "react";
import { Files } from "../lib/api";
import { Button } from "./ui/button";
import {
  FileText, Eye, Download, RefreshCw, Plus, ShieldCheck, X,
} from "lucide-react";
import { fmtDateTime } from "../lib/auditFormat";
import { toast } from "sonner";
import { getUploadPolicy, validateUpload, downloadUrlFor } from "../lib/uploadPolicy";

const META = {
  aadhar:               { label: "Aadhar Card",        group: "customer", required: true },
  pan:                  { label: "PAN Card",           group: "customer", required: false },
  passport_photo:       { label: "Passport Photo",     group: "customer", required: true },
  vin_photo:            { label: "Chassis No · Photo", group: "vehicle",  required: false },
  vehicle_photo_front:  { label: "Vehicle · Front",    group: "vehicle",  required: false },
  vehicle_photo_left:   { label: "Vehicle · Left",     group: "vehicle",  required: false },
  vehicle_photo_right:  { label: "Vehicle · Right",    group: "vehicle",  required: false },
  vehicle_photo_rear:   { label: "Vehicle · Rear",     group: "vehicle",  required: false },
  pdi_check_sheet:      { label: "PDI Check Sheet",    group: "pdi",      required: false },
  tax_invoice:          { label: "Tax Invoice",        group: "finance",  required: false },
  tax_receipt:          { label: "Road Tax Receipt",   group: "delivery", required: false },
  hsrp_receipt:         { label: "HSRP Receipt",       group: "delivery", required: false },
  vas_pp_certificate:   { label: "PP Certificate",     group: "delivery", required: false },
  vas_pp_invoice:       { label: "PP Invoice",         group: "delivery", required: false },
  vas_amc_certificate:  { label: "AMC Certificate",    group: "delivery", required: false },
  vas_amc_invoice:      { label: "AMC Invoice",        group: "delivery", required: false },
  vas_rsa_policy:       { label: "RSA Policy",         group: "delivery", required: false },
  form_20:              { label: "Form 20",            group: "registration", required: false },
  form_22:              { label: "Form 22",            group: "registration", required: false },
  other:                { label: "Any Other Document", group: "other",    required: false, multiple: true },
  other_2:              { label: "Any Other Document 2", group: "other",  required: false, multiple: true },
};

const GROUP_TITLES = {
  customer: "Customer Documents",
  vehicle: "Vehicle Documents",
  finance: "Finance Documents",
  pdi: "PDI Documents",
  registration: "Registration Documents",
  delivery: "Delivery Documents",
  other: "Other Documents",
};

const ALL_TYPES = Object.keys(META);

export default function CompactDocumentGrid({ saleId, uploadedBy, readOnly = false, testid = "doc-grid" }) {
  const [filesByType, setFilesByType] = useState({});
  const refresh = async () => {
    if (!saleId) return;
    const all = await Files.list(saleId);
    const map = {};
    for (const f of all) (map[f.type] ||= []).push(f);
    setFilesByType(map);
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [saleId]);

  const grouped = useMemo(() => {
    const g = {};
    for (const t of ALL_TYPES) {
      const k = META[t].group;
      (g[k] ||= []).push(t);
    }
    return g;
  }, []);

  if (!saleId) {
    return (
      <div className="rounded-od-input border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        Save the sale as a draft first to start uploading documents.
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid={testid}>
      {Object.entries(GROUP_TITLES).map(([group, title]) => {
        const types = grouped[group] || [];
        if (types.length === 0) return null;
        return (
          <section key={group}>
            <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
              {title}
            </h4>
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-4">
              {types.flatMap((t) => {
                const meta = META[t];
                const list = filesByType[t] || [];
                if (meta.multiple) {
                  return [
                    ...list.map((f) => (
                      <DocCard
                        key={f.id} type={t} meta={meta} file={f}
                        refresh={refresh} readOnly={readOnly}
                        saleId={saleId} uploadedBy={uploadedBy}
                      />
                    )),
                    <UploadTile key={`${t}-add`} type={t} meta={meta} saleId={saleId}
                      uploadedBy={uploadedBy} refresh={refresh} readOnly={readOnly}
                      labelOverride={list.length > 0 ? "Upload another" : null}
                    />,
                  ];
                }
                const file = list[0];
                if (file) {
                  return [<DocCard key={t} type={t} meta={meta} file={file}
                                   refresh={refresh} readOnly={readOnly}
                                   saleId={saleId} uploadedBy={uploadedBy} />];
                }
                return [<UploadTile key={t} type={t} meta={meta} saleId={saleId}
                                    uploadedBy={uploadedBy} refresh={refresh}
                                    readOnly={readOnly} />];
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function DocCard({ type, meta, file, refresh, readOnly, saleId, uploadedBy }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const policy = getUploadPolicy(type);

  const handleReplace = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const err = validateUpload(type, f);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      await Files.remove(file.id);
      await Files.upload(saleId, type, f, uploadedBy);
      toast.success("Replaced");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Replace failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this document?")) return;
    try { await Files.remove(file.id); refresh(); toast.success("Removed"); }
    catch (e) { toast.error("Delete failed"); }
  };

  const downloadUrl = downloadUrlFor(type, file, Files.url(file.id));

  return (
    <div
      className="group relative flex flex-col gap-2 rounded-od-input border border-slate-200 bg-white p-3 shadow-od-subtle"
      data-testid={`doc-card-${type}`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-50 border border-slate-200">
          <FileText className="h-4 w-4 text-slate-600" />
        </span>
        {meta.required && (
          <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700 border border-rose-200">
            <ShieldCheck className="h-2.5 w-2.5" /> Required
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-slate-900" title={file.original_filename || meta.label}>
          {meta.label}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-slate-500">
          {file.uploader_name || "—"}{file.uploaded_at ? ` · ${fmtDateTime(file.uploaded_at)}` : ""}
        </div>
      </div>
      <div className="mt-1 flex items-center gap-1">
        <a href={Files.url(file.id)} target="_blank" rel="noreferrer"
           className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900"
           title="Preview" data-testid={`doc-preview-${type}`}>
          <Eye className="h-3.5 w-3.5" />
        </a>
        <a href={downloadUrl} download
           className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900"
           title={policy.pdfOnDownload && (file.content_type || "").startsWith("image/") ? "Download as PDF" : "Download"}
           data-testid={`doc-download-${type}`}>
          <Download className="h-3.5 w-3.5" />
        </a>
        {!readOnly && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
            title="Replace" data-testid={`doc-replace-${type}`}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
          </button>
        )}
        {!readOnly && meta.multiple && (
          <button
            type="button"
            onClick={handleDelete}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-600 hover:bg-rose-50 hover:text-rose-600 ml-auto"
            title="Remove" data-testid={`doc-remove-${type}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" className="hidden"
             accept={policy.acceptAttr} onChange={handleReplace} />
    </div>
  );
}

function UploadTile({ type, meta, saleId, uploadedBy, refresh, readOnly, labelOverride }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const policy = getUploadPolicy(type);
  const handlePicked = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    for (const f of files) {
      const err = validateUpload(type, f);
      if (err) { toast.error(err); return; }
    }
    setBusy(true);
    try {
      for (const f of files) await Files.upload(saleId, type, f, uploadedBy);
      toast.success("Uploaded");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => !readOnly && inputRef.current?.click()}
        disabled={readOnly || busy}
        data-testid={`doc-upload-${type}`}
        className="flex flex-col items-center justify-center gap-1 rounded-od-input border-2 border-dashed border-slate-200 bg-slate-50/60 p-3 text-center transition-colors hover:border-primary hover:bg-primary/[0.03] disabled:cursor-not-allowed disabled:opacity-60 min-h-[112px]"
      >
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-white border border-slate-200">
        <Plus className="h-4 w-4 text-slate-600" />
      </span>
      <span className="text-xs font-semibold text-slate-700 leading-tight">
        {labelOverride || (meta.multiple ? `Upload ${meta.label}` : meta.label)}
      </span>
      {policy.pdfOnly && (
        <span className="text-[10px] text-slate-500">.pdf only</span>
      )}
      {meta.required && !labelOverride && (
        <span className="text-[10px] font-bold uppercase tracking-wider text-rose-600">Required</span>
      )}
      <input ref={inputRef} type="file" className="hidden"
             multiple={meta.multiple}
             accept={policy.acceptAttr} onChange={handlePicked} />
      </button>
      {policy.allowCamera && !readOnly && (
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={busy}
          className="inline-flex items-center justify-center gap-1 rounded-od-input border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
          data-testid={`doc-capture-${type}`}
        >
          <Plus className="h-3 w-3" /> Take Photo
        </button>
      )}
      {policy.allowCamera && (
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handlePicked}
        />
      )}
    </div>
  );
}
